import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDashboardByCounty, useDashboardByDistrict } from './useDashboard';

export const DATE_RANGES = [
  { label: 'Today', period: 'today' as const },
  { label: 'This week', period: 'this_week' as const },
  { label: 'Last 7 days', period: 'last_7_days' as const },
  { label: 'Last 30 days', period: 'last_30_days' as const },
  { label: 'Drive to date', period: 'drive_to_date' as const },
];

/**
 * Shared county/district/division/location + date-range filter state, used by
 * every page under the admin dashboard (Dashboard, Reports, Gaps, ...) so a
 * selection made in one place means the same thing everywhere — this is the
 * single source of truth the reports/gaps pages read to stay "identical" to
 * the main dashboard's filter bar instead of drifting into their own copy.
 */
export const useDashboardFilters = () => {
  const [selectedCounty, setSelectedCounty] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedDivision, setSelectedDivision] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [dateRange, setDateRange] = useState('Today');

  const period = DATE_RANGES.find((r) => r.label === dateRange)?.period || 'today';

  const countyMetrics = useDashboardByCounty();
  const districtMetrics = useDashboardByDistrict(selectedCounty || null);

  const counties = countyMetrics.data || [];
  const districts = districtMetrics.data || [];

  const uniqueCounties = useMemo(
    () => [...new Set(counties.map((c: any) => c.county).filter(Boolean))] as string[],
    [counties],
  );
  const uniqueDistricts = useMemo(() => {
    if (!selectedCounty) return [];
    return [...new Set(districts.map((d: any) => d.district).filter(Boolean))] as string[];
  }, [districts, selectedCounty]);

  const divisionsQuery = useQuery({
    queryKey: ['divisions-filter', selectedCounty, selectedDistrict],
    queryFn: async () => {
      if (!selectedCounty || !selectedDistrict) return [];
      const { apiClient } = await import('@services/api/client');
      const r = await (apiClient as any).get('/citizens/divisions/', { params: { county: selectedCounty, district: selectedDistrict } });
      return Array.isArray(r) ? r : [];
    },
    enabled: !!selectedCounty && !!selectedDistrict,
    staleTime: 300000,
  });

  const locationsQuery = useQuery({
    queryKey: ['locations-filter', selectedCounty, selectedDistrict, selectedDivision],
    queryFn: async () => {
      if (!selectedCounty || !selectedDistrict || !selectedDivision) return [];
      const { apiClient } = await import('@services/api/client');
      const r = await (apiClient as any).get('/citizens/locations/', { params: { county: selectedCounty, district: selectedDistrict, division: selectedDivision } });
      return Array.isArray(r) ? r : [];
    },
    enabled: !!selectedCounty && !!selectedDistrict && !!selectedDivision,
    staleTime: 300000,
  });

  const selectCounty = (v: string) => { setSelectedCounty(v); setSelectedDistrict(''); setSelectedDivision(''); setSelectedLocation(''); };
  const selectDistrict = (v: string) => { setSelectedDistrict(v); setSelectedDivision(''); setSelectedLocation(''); };
  const selectDivision = (v: string) => { setSelectedDivision(v); setSelectedLocation(''); };
  const clear = () => { setSelectedCounty(''); setSelectedDistrict(''); setSelectedDivision(''); setSelectedLocation(''); };

  return {
    selectedCounty, selectedDistrict, selectedDivision, selectedLocation,
    selectCounty, selectDistrict, selectDivision, selectLocation: setSelectedLocation, clear,
    dateRange, setDateRange, period,
    uniqueCounties, uniqueDistricts,
    uniqueDivisions: (divisionsQuery.data || []) as string[],
    uniqueLocations: (locationsQuery.data || []) as string[],
  };
};

export type DashboardFilters = ReturnType<typeof useDashboardFilters>;
